/* Hallmark · component: report-document · genre: editorial · theme: Arkadiah brand · P5 H5 E4 S5 R5 V4 */

import type { ReportTranslator } from '@/common/plugins/i18n.plugin';

/**
 * Shared branded chrome for the print-PDF reports (EUDR compliance,
 * deforestation forest-change). One place owns the Arkadiah Brand Guide
 * v1.1 styling — forest-green banner (#064635) with the reversed-white
 * ARKADIAH logo, Cormorant display / Helvetica Neue body, #4d4d4d grey
 * body text — so the two reports render identical chrome and a future
 * report can reuse it.
 *
 * Every colour is a locked `--color-*` token referenced by name; no
 * inline hex in the chrome. The per-report builders only supply their
 * meta rows + section bodies and call `renderReportDocument`.
 */

/** Escape the five HTML-significant chars for safe text interpolation. */
export const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Fixed-precision number formatting shared by the report builders
 * (always shows exactly `fractionDigits` decimals). Distinct from the
 * 0-decimal `formatNumber` in utils.helper. */
export function formatFixed(n: number, fractionDigits = 2): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** Default brand logo (Arkadiah wordmark + acacia tree). Served from
 * `public/`, so it needs an absolute origin URL inside the iframe
 * `srcdoc` document — relative paths don't resolve there. Reversed to
 * white via a CSS filter for the forest-green banner. */
function defaultLogoUrl(): string {
  return typeof window !== 'undefined'
    ? `${window.location.origin}/logo-green.png`
    : '/logo-green.png';
}

/**
 * The shared `<style>` block. Token block first (Arkadiah palette +
 * type pairing), then chrome rules that reference those tokens. Print
 * is a fixed-width A4 document — no responsive breakpoints needed.
 */
const REPORT_STYLES = `
    :root {
      --color-brand: #064635;
      --color-on-brand: #ffffff;
      --color-ink: #4d4d4d;
      --color-muted: #9e9e9e;
      --color-muted-2: #4e4e4e;
      --color-canvas: #f9f8f7;
      --color-paper: #ffffff;
      --color-paper-2: #fafaf8;
      --color-rule: #e0e0e0;
      --color-rule-soft: #f0ede8;
      --color-accent: #064635;
      --color-alert: #8a2e2e;
      --font-display: 'Cormorant', 'Big Caslon Medium', Georgia, serif;
      --font-body: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    }
    @page { size: A4; margin: 18mm; }
    body {
      font-family: var(--font-body);
      color: var(--color-ink);
      margin: 0;
      background: var(--color-canvas);
    }
    .container { background: var(--color-paper); padding: 32px 36px; }
    .brand {
      background: var(--color-brand);
      color: var(--color-on-brand);
      padding: 22px 28px;
      border-radius: 6px;
      margin: -8px -8px 24px -8px;
      /* Force the green band to print: Chrome's "Save as PDF" omits
         background graphics by default, and the reversed-white logo +
         white text would vanish on an unprinted background. */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .brand-logo {
      height: 26px;
      width: auto;
      display: block;
      margin-bottom: 12px;
      /* logo-green.png is dark green; flatten to a white silhouette so it
         reads on the forest-green banner. */
      filter: brightness(0) invert(1);
    }
    .brand .kicker {
      font-size: 11px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      opacity: 0.85;
    }
    .brand .subtitle { font-size: 13px; opacity: 0.92; margin-top: 2px; }
    h2 {
      font-family: var(--font-display);
      font-weight: 500;
      font-size: 18px;
      color: var(--color-brand);
      border-bottom: 1px solid var(--color-rule);
      padding-bottom: 6px;
      margin: 24px 0 10px;
    }
    .callout {
      border: 1px solid var(--color-rule);
      border-left: 6px solid var(--color-accent);
      padding: 16px 20px;
      border-radius: 6px;
      background: var(--color-paper-2);
      margin: 16px 0 20px;
    }
    .callout--alert { --color-accent: var(--color-alert); }
    .callout-label {
      color: var(--color-accent);
      font-weight: 600;
      font-size: 16px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .callout-figure { font-size: 22px; margin: 6px 0 4px; }
    .callout-meta { font-size: 12px; color: var(--color-muted-2); }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    table thead th {
      text-align: left;
      font-weight: 600;
      color: var(--color-ink);
      border-bottom: 1px solid var(--color-rule);
      padding: 6px 8px;
    }
    table td { padding: 5px 8px; border-bottom: 1px solid var(--color-rule-soft); }
    td.muted { text-align: center; color: var(--color-muted); font-style: italic; }
    .meta-grid { display: table; width: 100%; font-size: 12px; }
    .meta-grid > div { display: table-row; }
    .meta-grid > div > span:first-child {
      display: table-cell;
      color: var(--color-muted-2);
      padding: 3px 12px 3px 0;
      width: 240px;
    }
    .meta-grid > div > span:last-child {
      display: table-cell;
      color: var(--color-ink);
      padding: 3px 0;
    }
    .methodology {
      font-size: 11px;
      color: var(--color-muted-2);
      line-height: 1.55;
      padding-left: 18px;
    }
    footer {
      font-size: 10px;
      color: var(--color-muted);
      margin-top: 28px;
      border-top: 1px solid var(--color-rule);
      padding-top: 10px;
    }
    @media print {
      body { background: var(--color-paper); }
      .container { padding: 0; }
    }`;

/** Builds the meta-grid block. Each cell is escaped here, so callers pass
 * raw label/value strings (no pre-escaping). */
function buildMetaGrid(rows: Array<[string, string]>): string {
  const body = rows
    .map(
      ([label, value]) =>
        `<div><span>${escapeHtml(label)}</span><span>${escapeHtml(value)}</span></div>`,
    )
    .join('');
  return `<div class="meta-grid">${body}</div>`;
}

export interface CalloutOptions {
  /** Uppercase eyebrow label, e.g. the verdict or "Total forest loss". */
  label: string;
  /** The headline figure line. */
  figure: string;
  /** The supporting sub-line. */
  meta: string;
  /** Render the alert (brand-red) accent instead of brand green. */
  isAlert?: boolean;
}

/** The shared 3-line callout panel (label / figure / meta). Text fields
 * are escaped here, so callers pass raw strings. */
export function buildCallout(opts: CalloutOptions): string {
  const className = opts.isAlert ? 'callout callout--alert' : 'callout';
  return `<div class="${className}">
      <div class="callout-label">${escapeHtml(opts.label)}</div>
      <div class="callout-figure">${escapeHtml(opts.figure)}</div>
      <div class="callout-meta">${escapeHtml(opts.meta)}</div>
    </div>`;
}

export interface ReportDocumentOptions {
  /** Document `<title>` (browser tab / print header). */
  title: string;
  /** Banner eyebrow, e.g. "EUDR Compliance Assessment". */
  kicker: string;
  /** Banner sub-line under the logo. */
  subtitle: string;
  /** Meta-grid label/value pairs (raw strings — escaped internally). */
  metaRows: Array<[string, string]>;
  /** Inner section HTML: callout panel + tables + methodology. */
  sections: string;
  /** Footer label, e.g. "EUDR Compliance Toolbox". */
  footerLabel: string;
  /** Pre-formatted generated date (raw string — escaped internally). */
  date: string;
  /** Report-language translator for the shared footer chrome. Reports not yet
   * localized pass an English-bound translator (`makeReportTranslator(Language.EN_US)`). */
  t: ReportTranslator;
  /** Override the brand logo URL (defaults to `${origin}/logo-green.png`). */
  logoUrl?: string;
}

/**
 * Assembles the full branded HTML document for a print-PDF report. The
 * forest-green banner + reversed-white logo, type pairing, palette, and
 * footer are owned here; callers supply only their data.
 *
 * Open the returned HTML via `printReportHtml` (hidden iframe → native
 * print dialog → Save as PDF). The iframe `load` event waits for the
 * banner logo, and `printReportHtml` waits for Cormorant, before
 * printing.
 */
export function renderReportDocument(opts: ReportDocumentOptions): string {
  const logoUrl = opts.logoUrl ?? defaultLogoUrl();
  const footerText = opts.t.html('Arkadiah — {label} · Generated {date}', {
    label: opts.footerLabel,
    date: opts.date,
  });
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(opts.title)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Cormorant:wght@500;600&display=swap" rel="stylesheet">
  <style>${REPORT_STYLES}
  </style>
</head>
<body>
  <div class="container">
    <header class="brand">
      <img class="brand-logo" src="${logoUrl}" alt="Arkadiah" />
      <div class="kicker">${escapeHtml(opts.kicker)}</div>
      <div class="subtitle">${escapeHtml(opts.subtitle)}</div>
    </header>

    ${buildMetaGrid(opts.metaRows)}

    ${opts.sections}

    <footer>
      ${footerText}
    </footer>
  </div>
</body>
</html>`;
}
